import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';

import {
  DASHBOARD_LAYOUT_STORAGE_KEY,
  DASHBOARD_LAYOUT_STORAGE_KEY_V8,
  DEFAULT_DASHBOARD_PRESET_ID,
  getPresetWidgetIds,
  isDashboardPresetId,
  isDashboardWidgetId,
  isDashboardWidgetSpan,
  normalizeWidgetSpan,
  type DashboardPresetId,
  type DashboardWidgetId,
  type DashboardWidgetSpan,
} from './dashboardPresets.js';

export interface LayoutItem {
  id: DashboardWidgetId;
  /** Optional desktop width override. `undefined` means "use the registry default". */
  spanOverride?: DashboardWidgetSpan;
}

interface PersistedDashboardLayout {
  activePresetId: DashboardPresetId;
  layout: LayoutItem[];
}

export interface DashboardLayoutState {
  activePresetId: DashboardPresetId;
  layout: LayoutItem[];
  applyPreset: (presetId: DashboardPresetId) => void;
  resetLayout: () => void;
  addWidget: (widgetId: DashboardWidgetId) => void;
  removeWidget: (widgetId: DashboardWidgetId) => void;
  moveWidget: (widgetId: DashboardWidgetId, direction: -1 | 1) => void;
  setWidgetSpan: (widgetId: DashboardWidgetId, span: DashboardWidgetSpan | null) => void;
}

const memoryStorage: Record<string, string> = {};

function getStorage(): {
  getItem: (k: string) => string | null;
  setItem: (k: string, v: string) => void;
  removeItem: (k: string) => void;
} {
  try {
    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      typeof window.localStorage.getItem === 'function'
    ) {
      return window.localStorage;
    }
  } catch {
    // fallback
  }
  return {
    getItem: (k: string) => memoryStorage[k] ?? null,
    setItem: (k: string, v: string) => {
      memoryStorage[k] = v;
    },
    removeItem: (k: string) => {
      delete memoryStorage[k];
    },
  };
}

function normalizeLayoutItem(item: unknown): LayoutItem | null {
  if (typeof item === 'string') {
    if (!isDashboardWidgetId(item)) return null;
    return { id: item };
  }

  if (typeof item !== 'object' || item === null) {
    return null;
  }

  const candidate = item as { id?: unknown; spanOverride?: unknown };
  if (typeof candidate.id !== 'string' || !isDashboardWidgetId(candidate.id)) {
    return null;
  }

  const layoutItem: LayoutItem = { id: candidate.id };

  if (typeof candidate.spanOverride === 'number' && isDashboardWidgetSpan(candidate.spanOverride)) {
    layoutItem.spanOverride = candidate.spanOverride;
  }

  return layoutItem;
}

function parsePersistedLayout(raw: string): PersistedDashboardLayout | null {
  const parsed: unknown = JSON.parse(raw);

  // v8 and earlier: plain array of widget ids.
  if (Array.isArray(parsed)) {
    const legacyLayout = parsed
      .map((widgetId) => normalizeLayoutItem(widgetId))
      .filter((item): item is LayoutItem => item !== null);
    return {
      activePresetId: DEFAULT_DASHBOARD_PRESET_ID,
      layout: legacyLayout,
    };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }

  const candidate = parsed as { activePresetId?: unknown; layout?: unknown };
  const activePresetId =
    typeof candidate.activePresetId === 'string' && isDashboardPresetId(candidate.activePresetId)
      ? candidate.activePresetId
      : DEFAULT_DASHBOARD_PRESET_ID;

  const layout: LayoutItem[] = Array.isArray(candidate.layout)
    ? candidate.layout
        .map((item) => normalizeLayoutItem(item))
        .filter((item): item is LayoutItem => item !== null)
    : [];

  return { activePresetId, layout };
}

function readPersistedLayout(): PersistedDashboardLayout | null {
  const storage = getStorage();

  if (!storage) {
    return null;
  }

  try {
    const raw =
      storage.getItem(DASHBOARD_LAYOUT_STORAGE_KEY) ??
      storage.getItem(DASHBOARD_LAYOUT_STORAGE_KEY_V8);
    return raw ? parsePersistedLayout(raw) : null;
  } catch {
    return null;
  }
}

function persistLayout(activePresetId: DashboardPresetId, layout: readonly LayoutItem[]): void {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  try {
    storage.setItem(
      DASHBOARD_LAYOUT_STORAGE_KEY,
      JSON.stringify({
        activePresetId,
        layout,
      }),
    );
  } catch {
    // localStorage may be disabled; the dashboard can still operate in-memory.
  }
}

function getDefaultLayoutItem(widgetId: DashboardWidgetId): LayoutItem {
  return { id: widgetId };
}

function dedupeLayout(layout: readonly LayoutItem[]): LayoutItem[] {
  const seen = new Set<DashboardWidgetId>();
  const result: LayoutItem[] = [];
  for (const item of layout) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      result.push(item);
    }
  }
  return result;
}

export function createDashboardLayoutStore(): StoreApi<DashboardLayoutState> {
  const persisted = readPersistedLayout();
  const initialPresetId = persisted?.activePresetId ?? DEFAULT_DASHBOARD_PRESET_ID;
  const initialLayout = persisted?.layout.length
    ? dedupeLayout(persisted.layout)
    : getPresetWidgetIds(initialPresetId).map(getDefaultLayoutItem);

  return createStore<DashboardLayoutState>((set, get) => ({
    activePresetId: initialPresetId,
    layout: initialLayout,
    applyPreset: (presetId) => {
      const nextLayout = getPresetWidgetIds(presetId).map(getDefaultLayoutItem);
      set({ activePresetId: presetId, layout: nextLayout });
      persistLayout(presetId, nextLayout);
    },
    resetLayout: () => {
      const { activePresetId } = get();
      const nextLayout = getPresetWidgetIds(activePresetId).map(getDefaultLayoutItem);
      set({ layout: nextLayout });
      persistLayout(activePresetId, nextLayout);
    },
    addWidget: (widgetId) => {
      const { activePresetId, layout } = get();

      if (layout.some((item) => item.id === widgetId)) {
        return;
      }

      const presetOrder = getPresetWidgetIds(activePresetId);
      const insertionIndex = presetOrder.indexOf(widgetId);
      const newItem = getDefaultLayoutItem(widgetId);
      const nextLayout = [...layout];

      if (insertionIndex === -1) {
        nextLayout.push(newItem);
      } else {
        nextLayout.splice(Math.min(insertionIndex, nextLayout.length), 0, newItem);
      }

      set({ layout: nextLayout });
      persistLayout(activePresetId, nextLayout);
    },
    removeWidget: (widgetId) => {
      const { activePresetId, layout } = get();
      const nextLayout = layout.filter((item) => item.id !== widgetId);
      set({ layout: nextLayout });
      persistLayout(activePresetId, nextLayout);
    },
    moveWidget: (widgetId, direction) => {
      const { activePresetId, layout } = get();
      const index = layout.findIndex((item) => item.id === widgetId);
      const targetIndex = index + direction;

      if (index === -1 || targetIndex < 0 || targetIndex >= layout.length) {
        return;
      }

      const nextLayout = [...layout];
      const currentItem = nextLayout[index]!;
      nextLayout[index] = nextLayout[targetIndex]!;
      nextLayout[targetIndex] = currentItem;
      set({ layout: nextLayout });
      persistLayout(activePresetId, nextLayout);
    },
    setWidgetSpan: (widgetId, span) => {
      const { activePresetId, layout } = get();
      const index = layout.findIndex((item) => item.id === widgetId);
      if (index === -1) return;

      const nextLayout = [...layout];
      const item = { ...nextLayout[index]! };
      if (span === null) {
        delete item.spanOverride;
      } else {
        item.spanOverride = span;
      }
      nextLayout[index] = item;
      set({ layout: nextLayout });
      persistLayout(activePresetId, nextLayout);
    },
  }));
}

const dashboardLayoutStore = createDashboardLayoutStore();

export function useDashboardLayoutStore<T>(selector: (state: DashboardLayoutState) => T): T {
  return useStore(dashboardLayoutStore, selector);
}

export function resetDashboardLayoutStoreForTest(): void {
  const nextLayout = getPresetWidgetIds(DEFAULT_DASHBOARD_PRESET_ID).map(getDefaultLayoutItem);
  dashboardLayoutStore.setState({
    activePresetId: DEFAULT_DASHBOARD_PRESET_ID,
    layout: nextLayout,
  });
}

/** Directly set state on the singleton store. Useful for component tests that need
 *  a specific layout without depending on localStorage. */
export function setDashboardLayoutStoreForTest(partial: Partial<DashboardLayoutState>): void {
  dashboardLayoutStore.setState(partial);
}

export type { DashboardWidgetSpan };
export { normalizeWidgetSpan };
